import { useQuery } from "@tanstack/react-query";
import { Alert, Button, Card, Col, Descriptions, Progress, Row, Skeleton, Space, Tag, Typography } from "antd";
import { api, unwrap } from "@/api/client";
import { useAuth } from "@/auth/AuthProvider";
import { useI18n } from "@/i18n";
import { formatBytes } from "@/lib/bytes";

type Sub = Awaited<ReturnType<typeof fetchSubscription>>;
type Quota = Sub["quotas"][number];

const fetchSubscription = () => unwrap(api.GET("/v1/workspace/subscription"));

/** The workspace's subscription, shared by the page and the header warning. */
export function useSubscription(enabled = true) {
  return useQuery({ queryKey: ["subscription"], queryFn: fetchSubscription, staleTime: 5 * 60_000, enabled });
}

/**
 * Langganan (Plan §6.17): the plan, its dates, and each quota with what is
 * used. Billing is manual for now: upgrading goes through the Zerpid team.
 */
export function SubscriptionPage() {
  const { t, formatDate, formatNumber } = useI18n();
  const { me } = useAuth();
  const q = useSubscription();
  const tz = me?.workspace?.timezone;
  const s = q.data;
  if (!s) return <Skeleton active />;

  return (
    <>
      <Typography.Title level={2} style={{ marginTop: 0, fontSize: 28 }}>
        {t("sub.title")}
      </Typography.Title>
      <SubscriptionWarnings sub={s} />
      <Row gutter={[16, 16]}>
        <Col xs={24} lg={9}>
          <Card title={t("sub.plan")}>
            <Descriptions column={1} size="small">
              <Descriptions.Item label={t("sub.plan")}>
                <Tag color="geekblue">{t(`plan.${s.plan}`)}</Tag>
              </Descriptions.Item>
              {s.plan === "trial" && <Descriptions.Item label={t("sub.trialEnds")}>{s.trialEndsAt ? formatDate(s.trialEndsAt, tz) : "—"}</Descriptions.Item>}
              {s.plan !== "trial" && s.plan !== "free" && <Descriptions.Item label={t("sub.planUntil")}>{s.planUntil ? formatDate(s.planUntil, tz) : t("sub.noEnd")}</Descriptions.Item>}
            </Descriptions>
            <Typography.Paragraph type="secondary" style={{ marginTop: 12 }}>
              {t(s.plan === "trial" ? "sub.trialHelp" : "sub.manualHelp")}
            </Typography.Paragraph>
            <Space wrap>
              {s.contact ? (
                <Button type="primary" href={s.contact} target="_blank" rel="noopener">
                  {t("sub.contact")}
                </Button>
              ) : (
                <Typography.Text strong>{t("sub.contactText")}</Typography.Text>
              )}
            </Space>
          </Card>
        </Col>
        <Col xs={24} lg={15}>
          <Card title={t("sub.usage")}>
            <Space orientation="vertical" size={16} style={{ width: "100%" }}>
              {s.quotas.map((x) => (
                <QuotaRow key={x.kind} quota={x} formatNumber={formatNumber} />
              ))}
            </Space>
            {s.ordersWaiting > 0 && (
              <Alert type="warning" showIcon style={{ marginTop: 16 }} title={t("sub.ordersWaiting", { n: formatNumber(s.ordersWaiting) })} />
            )}
          </Card>
        </Col>
      </Row>
    </>
  );
}

function QuotaRow({ quota, formatNumber: num }: { quota: Quota; formatNumber: (n: number) => string }) {
  const { t, locale } = useI18n();
  // Storage counts in MiB; shown as a size.
  const formatNumber = quota.kind === "storage_mb" ? (n: number) => formatBytes(n * 1024 * 1024, locale === "id" ? "id-ID" : "en-GB") : num;
  const pct = quota.limit ? Math.min(100, Math.round((quota.used / Math.max(quota.limit, 1)) * 100)) : 0;
  return (
    <div>
      <Space style={{ width: "100%", justifyContent: "space-between" }}>
        <Typography.Text>{t(`sub.kind.${quota.kind}`)}</Typography.Text>
        <Typography.Text className="z-num" type={quota.state === "full" ? "danger" : quota.state === "warn" ? "warning" : undefined}>
          {quota.limit === null ? t("sub.unlimited", { n: formatNumber(quota.used) }) : `${formatNumber(quota.used)} / ${formatNumber(quota.limit)}`}
        </Typography.Text>
      </Space>
      {quota.limit !== null && <Progress percent={pct} showInfo={false} status={quota.state === "full" ? "exception" : "normal"} size="small" />}
    </div>
  );
}

/** What to warn about: quotas at 80% or full, a trial or paid period ending. */
export function SubscriptionWarnings({ sub, compact }: { sub: Sub; compact?: boolean }) {
  const { t, formatDate } = useI18n();
  const { me } = useAuth();
  const tz = me?.workspace?.timezone;
  const full = sub.warnings.filter((w) => w.startsWith("quota_full:")).map((w) => t(`sub.kind.${w.slice(11) as Quota["kind"]}`));
  const warn = sub.warnings.filter((w) => w.startsWith("quota_warn:")).map((w) => t(`sub.kind.${w.slice(11) as Quota["kind"]}`));
  const items: { type: "error" | "warning"; text: string }[] = [];
  if (full.length) items.push({ type: "error", text: t("sub.warn.full", { kinds: full.join(", ") }) });
  if (warn.length) items.push({ type: "warning", text: t("sub.warn.near", { kinds: warn.join(", ") }) });
  if (sub.warnings.includes("trial_ending") && sub.trialEndsAt) items.push({ type: "warning", text: t("sub.warn.trial", { date: formatDate(sub.trialEndsAt, tz) }) });
  if (sub.warnings.includes("plan_ending") && sub.planUntil) items.push({ type: "warning", text: t("sub.warn.planEnding", { date: formatDate(sub.planUntil, tz) }) });
  if (sub.warnings.includes("plan_expired")) items.push({ type: "error", text: t("sub.warn.planExpired") });
  if (items.length === 0) return null;
  return (
    <Space orientation="vertical" style={{ width: "100%", marginBottom: compact ? 0 : 16 }}>
      {items.map((x) => (
        <Alert key={x.text} type={x.type} showIcon banner={compact} title={x.text} />
      ))}
    </Space>
  );
}
